import { useMemo } from 'react';
import { first } from 'lodash';
import { useNavigate } from 'react-router';
import { t } from 'i18next';
import { dateTimeDistanceToNow } from '@pocapan/shared/utils/DateUtil';
import { ellipsis } from '@pocapan/shared/utils/TextUtil';
import SecondaryButton from '../buttons/SecondaryButton';
import VerifiedIcon from '../icons/VerifiedIcon';

interface UserProfile {
  userId?: string;
  userProfileId: string;
  nickname: string;
  identified: boolean;
  pictureUrl?: string;
  thumbnailUrl?: string;
}

interface PhotoCard {
  artistId: string;
  mediumThumbnailUrl?: string;
  pictureUrl: string;
}

interface LastMessage {
  content: string;
  sentAt: string;
  unseenCounts?: Array<{ [key: string]: number }>;
}

interface ChatRoom {
  chatRoomId: string;
  subjectType?: string;
  myProfile: {
    userProfileId: string;
  };
  traderProfile?: UserProfile;
  otherUserProfile?: UserProfile;
  photoCard?: PhotoCard;
  lastMessage: LastMessage;
  unreadCount?: number;
}

interface ChatRoomV2Props {
  chatRoom: ChatRoom;
  onClick?: (chatRoomId: string) => void;
  hideImage?: boolean;
  showSelectButton?: boolean;
}

export default function ChatRoomV2({
  chatRoom,
  onClick,
  hideImage = false,
  showSelectButton = false
}: ChatRoomV2Props): JSX.Element | null {
  const navigate = useNavigate();

  const unSeenCount = useMemo(() => {
    // For DM chatrooms without unseenCounts structure
    if (!chatRoom.lastMessage?.unseenCounts) {
      return chatRoom.unreadCount || 0;
    }

    // TODO : Server API should be changed with proper UUID format
    const userProfileIdWithoutDashes = chatRoom.myProfile.userProfileId.replace(/-/g, '');
    const myUnSeenCount = chatRoom.lastMessage.unseenCounts.find((x) =>
      x.hasOwnProperty(userProfileIdWithoutDashes)
    );
    if (myUnSeenCount) return first(Object.values(myUnSeenCount));
    return 0;
  }, [chatRoom]);

  const isDMChatRoom = !chatRoom.subjectType;

  const onClickChatRoom = () => {
    if (onClick) {
      onClick(chatRoom.chatRoomId);
    } else if (isDMChatRoom) {
      navigate(`/chat_rooms/${chatRoom.chatRoomId}/v2`);
    } else {
      if (chatRoom?.photoCard?.artistId) {
        navigate(`/chats/${chatRoom.chatRoomId}?artistId=${chatRoom?.photoCard?.artistId}`);
      } else {
        navigate(`/chats/${chatRoom.chatRoomId}`);
      }
    }
  };

  const currentProfile = isDMChatRoom ? chatRoom.otherUserProfile : chatRoom.traderProfile;
  const profileImageUrl = currentProfile?.pictureUrl || currentProfile?.thumbnailUrl;

  if (!chatRoom) return null;

  return (
    <div
      className="flex items-center cursor-pointer"
      key={chatRoom.chatRoomId}
      onClick={() => onClickChatRoom()}
    >
      <div className="flex-none">
        {profileImageUrl ? (
          <img
            className="w-[38px] h-[38px] rounded-full object-cover"
            src={profileImageUrl}
            alt="Profile"
            loading="lazy"
          />
        ) : (
          <div className="w-[38px] h-[38px] rounded-full bg-gradient-to-br from-purple-400 to-purple-600 opacity-26" />
        )}
      </div>

      <div className="flex-1 pl-[14px]">
        <div className="flex flex-col items-start space-y-0">
          <div className="flex items-center space-x-0">
            <span className="text-main-light-2 dark:text-main-dark-2 font-semibold text-base leading-tight truncate">
              {ellipsis(currentProfile?.nickname || '', 15)}
            </span>
            {currentProfile?.identified && (
              <VerifiedIcon width="14px" height="14px" className="ml-0.5" />
            )}
            <span className="text-main-light-4 dark:text-main-dark-4 text-xs font-medium pl-2">
              {dateTimeDistanceToNow(chatRoom.lastMessage.sentAt)}
            </span>
          </div>

          <span className="text-main-light-4 dark:text-main-dark-4 text-[15px] font-medium leading-tight truncate">
            {chatRoom.lastMessage.content}
          </span>
        </div>
      </div>

      {unSeenCount && unSeenCount > 0 && (
        <div className="rounded-[30px] bg-primary-light/15 dark:bg-primary-dark/15 text-primary-light dark:text-primary-dark px-2 py-1.5 mr-3 text-[13px] font-semibold">
          {unSeenCount}
        </div>
      )}

      {!hideImage && chatRoom.subjectType === 'MatchedTradingPhotoCard' && (
        <div className="flex-none">
          <img
            className="w-[37px] h-[58px] rounded-md object-cover"
            src={chatRoom.photoCard?.mediumThumbnailUrl || chatRoom.photoCard?.pictureUrl}
            alt="Photo Card"
            loading="lazy"
          />
        </div>
      )}

      {showSelectButton && (
        <SecondaryButton
          text={String(t('select'))}
          icon={<></>}
          leftIcon={<></>}
          disabled={false}
        />
      )}
    </div>
  );
}
